import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";
import { Bot, Lock } from "lucide-react";
import { cn } from "@/lib/cn";
import { num } from "@/lib/format";
import { EmptyState, ProgressBar } from "@/components/ui";

// Small building blocks shared by the AI screens. The markdown renderer builds React nodes (never raw HTML), so model
// output can never inject markup.

const APP_PATH = /(\/(?:[a-z0-9-]+)(?:\/[a-z0-9-]+)*)/g;

function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  text.split(/(\*\*[^*]+\*\*)/g).forEach((part, i) => {
    if (!part) return;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      out.push(
        <strong key={`${keyPrefix}-b${i}`} className="font-semibold text-ink">
          {part.slice(2, -2)}
        </strong>,
      );
      return;
    }
    part.split(APP_PATH).forEach((chunk, j) => {
      if (!chunk) return;
      if (/^\/[a-z0-9-]+(\/[a-z0-9-]+)*$/.test(chunk) && chunk.length > 2) {
        out.push(
          <Link key={`${keyPrefix}-l${i}-${j}`} to={chunk} className="text-primary-400 underline-offset-2 hover:underline">
            {chunk}
          </Link>,
        );
      } else out.push(<Fragment key={`${keyPrefix}-t${i}-${j}`}>{chunk}</Fragment>);
    });
  });
  return out;
}

/** Renders the plain text answers of the assistant: paragraphs, bullet and numbered lists, bold, app links. */
export function AiMarkdown({ text, className }: { text: string; className?: string }) {
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={`list-${blocks.length}`} className={cn("space-y-1 pl-5", list.ordered ? "list-decimal" : "list-disc")}>
        {list.items.map((item, i) => (
          <li key={i}>{inline(item, `li-${blocks.length}-${i}`)}</li>
        ))}
      </Tag>,
    );
    list = null;
  };
  text.split(/\r?\n/).forEach((raw, index) => {
    const line = raw.trim();
    const bullet = /^[-*•]\s+(.*)$/.exec(line);
    const ordered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || ordered) {
      const isOrdered = Boolean(ordered);
      if (list && list.ordered !== isOrdered) flush();
      list ??= { ordered: isOrdered, items: [] };
      list.items.push((bullet ?? ordered)![1]!);
      return;
    }
    flush();
    if (!line) return;
    const heading = /^#{1,4}\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push(
        <p key={`h-${index}`} className="font-semibold text-ink">
          {inline(heading[1]!, `h-${index}`)}
        </p>,
      );
      return;
    }
    blocks.push(<p key={`p-${index}`}>{inline(line, `p-${index}`)}</p>);
  });
  flush();
  return <div className={cn("space-y-2 break-words text-[13.5px] leading-relaxed", className)}>{blocks}</div>;
}

export function AiAvatar({ size = "md", className }: { size?: "sm" | "md" | "lg"; className?: string }) {
  const dims = size === "sm" ? "size-7 [&>svg]:size-3.5" : size === "lg" ? "size-11 [&>svg]:size-5.5" : "size-9 [&>svg]:size-4.5";
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1d4ed8] to-[#2f6bff] text-white shadow-[0_6px_18px_-8px_rgb(47_107_255/0.9)]", dims, className)}>
      <Bot aria-hidden />
    </span>
  );
}

export function NoAccess({ title = "Accès réservé", description, compact }: { title?: string; description: string; compact?: boolean }) {
  return <EmptyState compact={compact} icon={<Lock className="size-5" />} title={title} description={description} />;
}

export function fmtTokens(value: number | null | undefined): string {
  return `${num(value ?? 0)} tokens`;
}

export function UsageMeter({ label, used, limit, hint, className }: { label: ReactNode; used: number; limit: number | null; hint?: ReactNode; className?: string }) {
  const ratio = limit && limit > 0 ? used / limit : 0;
  const tone = limit === null ? "blue" : ratio >= 1 ? "red" : ratio >= 0.8 ? "orange" : "green";
  return (
    <div className={cn("min-w-0", className)}>
      <div className="mb-1.5 flex items-baseline justify-between gap-3 text-[13px]">
        <span className="truncate text-ink-2">{label}</span>
        <span className="shrink-0 font-semibold tabular text-ink">
          {num(used)}
          {limit !== null ? <span className="font-normal text-muted"> / {num(limit)}</span> : null}
        </span>
      </div>
      <ProgressBar value={limit === null ? 0 : Math.min(used, limit)} max={limit && limit > 0 ? limit : 1} tone={tone} />
      {hint ? <p className="mt-1 text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

/** Circular score (Business Brain completeness). */
export function ScoreRing({ score, size = 132, label = "complet" }: { score: number; size?: number; label?: string }) {
  const stroke = 11;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const value = Math.max(0, Math.min(100, score));
  const color = value >= 80 ? "#22c55e" : value >= 50 ? "#2f6bff" : "#f59e0b";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#16223b" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(value / 100) * c} ${c}`}
          style={{ filter: `drop-shadow(0 0 6px ${color}66)`, transition: "stroke-dasharray 600ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" role="img" aria-label={`${value} % ${label}`}>
        <span className="text-[28px] font-bold leading-none tracking-tight text-white tabular">{value}%</span>
        <span className="mt-1 text-[11.5px] text-muted">{label}</span>
      </div>
    </div>
  );
}

/** Small label / value tile used in settings and summaries. */
export function StatTile({ label, value, icon, className }: { label: ReactNode; value: ReactNode; icon?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex min-w-0 items-center gap-3 rounded-xl border border-line bg-panel-2/60 px-3.5 py-3", className)}>
      {icon ? <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary-300 [&>svg]:size-4.5">{icon}</span> : null}
      <div className="min-w-0">
        <p className="truncate text-xs text-muted">{label}</p>
        <p className="truncate text-[15px] font-semibold text-ink tabular">{value}</p>
      </div>
    </div>
  );
}
